import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Swords, Crown, Zap, Info, ArrowRight, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty, Card } from "../components/UI";
import { ShareButton } from "../components/ShareButton";

const CONTAINER = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";

// Two instances of the SAME championship machinery/UI — differ only by data.
const VARIANTS = {
  ENC: {
    endpoint: "/nations/championship",
    breadcrumb: "European Nations Championship",
    eyebrow: "ESBF · Continental Nations",
    title: "European Nations Championship",
    tagline: "10 national teams · two groups of five · top two advance to the knockout stage. Organised by the European Sports Trading Federation.",
    championLabel: "Champions of Europe",
    shareId: "nations",
    showHistory: true,
    historyEndpoint: "/nations/championship/history",
    historyTitle: "European Nations History",
    historySubtitle: "Past champions — every completed edition, on record forever",
    historyEmptyText: "No European Nations Championship has been completed yet. Past champions will appear here as editions conclude.",
  },
  WORLDS: {
    endpoint: "/worlds",
    breadcrumb: "World Nations Championship",
    eyebrow: "WSTF · World Nations",
    title: "World Nations Championship",
    tagline: "All 10 national teams · two groups of five · top two advance to the knockout stage. The WSTF world title for national teams — independent of the European Nations Championship.",
    championLabel: "World Nations Champions",
    shareId: "worlds",
    showHistory: true,
    historyEndpoint: "/worlds/history",
    historyTitle: "World Nations History",
    historySubtitle: "Past champions — every completed edition, on record forever",
    historyEmptyText: "No World Nations Championship has been completed yet. Past champions will appear here as editions conclude.",
  },
};

// Group standings — mobile prioritises POS/TEAM/PTS; secondary stats appear from sm/md up.
function GroupTable({ group }) {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden" data-testid={`group-table-${group.code}`}>
      <div className="px-4 py-2.5 bg-[#070707] font-heading font-black uppercase tracking-wide text-slate-100 flex items-center justify-between">
        <span>Group {group.code}</span>
        <span className="text-[10px] font-mono text-emerald-400 normal-case tracking-normal">Top 2 advance</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-[#070707]">
            <th scope="col" className="px-3 py-2 text-left w-10">#</th>
            <th scope="col" className="px-2 py-2 text-left">Team</th>
            <th scope="col" className="px-2 py-2 text-right hidden sm:table-cell">P</th>
            <th scope="col" className="px-2 py-2 text-right hidden sm:table-cell">W</th>
            <th scope="col" className="px-2 py-2 text-right hidden sm:table-cell">D</th>
            <th scope="col" className="px-2 py-2 text-right hidden sm:table-cell">L</th>
            <th scope="col" className="px-2 py-2 text-right hidden md:table-cell">TPF</th>
            <th scope="col" className="px-2 py-2 text-right hidden md:table-cell">TPA</th>
            <th scope="col" className="px-2 py-2 text-right hidden md:table-cell">TPD</th>
            <th scope="col" className="px-3 py-2 text-right text-[#E5B842]">PTS</th>
          </tr>
        </thead>
        <tbody>
          {group.standings.map((r) => (
            <tr key={r.nt_id} data-testid={`group-${group.code}-row-${r.position}`} className="border-t border-[#2b2512]/60">
              <td className="px-3 py-2.5 relative font-heading font-black text-slate-200">
                {r.position <= 2 && <span className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" aria-label="Qualifies" />}
                {r.position}{r.position <= 2 && <span className="ml-1 text-emerald-400 text-[9px] align-top font-mono">Q</span>}
              </td>
              <td className="px-2 py-2.5">
                <Link to={`/nations/${r.team.country_iso3}`} className="flex items-center gap-2 text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                  <span className="text-lg" aria-hidden="true"><Flag country={r.team} width={22} /></span>
                  <span className="font-semibold">{r.team.sporty}</span>
                </Link>
              </td>
              <td className="px-2 py-2.5 text-right text-slate-400 hidden sm:table-cell">{r.played}</td>
              <td className="px-2 py-2.5 text-right text-emerald-400 hidden sm:table-cell">{r.won}</td>
              <td className="px-2 py-2.5 text-right text-slate-300 hidden sm:table-cell">{r.drawn}</td>
              <td className="px-2 py-2.5 text-right text-rose-400 hidden sm:table-cell">{r.lost}</td>
              <td className="px-2 py-2.5 text-right text-slate-400 hidden md:table-cell">{r.tpf}</td>
              <td className="px-2 py-2.5 text-right text-slate-400 hidden md:table-cell">{r.tpa}</td>
              <td className="px-2 py-2.5 text-right text-slate-300 tabular-nums hidden md:table-cell">{r.tpd > 0 ? `+${r.tpd}` : r.tpd}</td>
              <td className="px-3 py-2.5 text-right font-heading font-black text-[#E5B842] text-base">{r.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KoCard({ ko, big }) {
  const decided = ko.status === "DECIDED";
  return (
    <div className={`rounded-xl bg-[#0f0f0f] border p-4 ${big ? "foil-border" : "border-[#2b2512]"}`} data-testid={`bracket-${ko.round}-${ko.bracket_index}`}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#E5B842] mb-2 flex items-center justify-between">
        <span>{ko.label}</span>
        {ko.extra_rounds > 0 && <span className="inline-flex items-center gap-1 text-fuchsia-400"><Zap className="w-3 h-3" /> Extra ×{ko.extra_rounds}</span>}
      </div>
      {["home", "away"].map((s) => {
        const t = ko[s];
        const pts = s === "home" ? ko.final_home_points : ko.final_away_points;
        const win = decided && ko.winner_nt_id === t?.id;
        return (
          <div key={s} className={`flex items-center gap-2 py-1.5 ${win ? "text-[#E5B842] font-bold" : "text-slate-200"}`}>
            <span className="text-lg" aria-hidden="true"><Flag country={t} width={22} /></span>
            <span className="flex-1 font-semibold truncate">{t?.name || "TBD"}</span>
            {decided && <span className="font-heading font-black tabular-nums">{pts}</span>}
            {win && <Crown className="w-4 h-4 text-[#E5B842]" />}
          </div>
        );
      })}
      {decided && <div className="mt-1 text-[9px] font-mono uppercase text-slate-600 text-right">Team Points</div>}
    </div>
  );
}

const STAGE_TABS = [
  { key: "all", label: "All" },
  { key: "A", label: "Group A" },
  { key: "B", label: "Group B" },
  { key: "KO", label: "Knockout" },
];
function stageOf(ctx) {
  const c = ctx || "";
  if (c.startsWith("Group A")) return "A";
  if (c.startsWith("Group B")) return "B";
  if (c === "Final" || c.startsWith("Semi")) return "KO";
  if (c.startsWith("Demonstration")) return "DEMO";
  return "other";
}

export default function ChampionshipPage({ variant = "ENC" }) {
  const V = VARIANTS[variant] || VARIANTS.ENC;
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [stage, setStage] = useState("all");

  useEffect(() => {
    setData(null);
    const load = () => api.get(V.endpoint).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [V.endpoint]);

  useEffect(() => {
    if (!V.showHistory) { setHistory(null); return; }
    api.get(V.historyEndpoint).then(({ data }) => setHistory(data)).catch(() => setHistory({ editions: [] }));
  }, [V.showHistory, V.historyEndpoint]);

  if (!data) return <Loader label="Loading championship…" />;
  if (data.error) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="Championship not available yet." /></div>;

  const sf = data.bracket.filter((b) => b.round === "SF");
  const final = data.bracket.find((b) => b.round === "FINAL");
  // Exclude the demonstration fixture from the main stream — it has its own clearly-labeled section.
  const fixtures = data.fixtures.filter((f) => stageOf(f.context_label) !== "DEMO");
  const shown = fixtures.filter((f) => stage === "all" || stageOf(f.context_label) === stage);

  return (
    <div className="animate-fade-up pb-20">
      {/* HEADER */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-gradient-to-b from-[#141007] to-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.14] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 130% at 80% 0%, rgba(212,175,55,0.16) 0%, transparent 55%)" }} />
        <div className={`${CONTAINER} relative py-10 sm:py-12`}>
          <nav className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-4">
            <Link to="/nations" className="hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>Nations</Link>
            <ArrowRight className="w-3 h-3 text-slate-700" />
            <span className="text-slate-300">{V.breadcrumb}</span>
          </nav>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">{V.eyebrow}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">{V.title}</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-3xl">{V.tagline}</p>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        {/* CHAMPION */}
        {data.champion && (
          <div data-testid="champion-banner" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-6 mb-8 flex items-center gap-5">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 160% at 10% 0%, rgba(212,175,55,0.18) 0%, transparent 60%)" }} />
            <Trophy className="relative w-11 h-11 text-[#E5B842] shrink-0" />
            <Link to={`/nations/${data.champion.country_iso3}`} className="relative flex items-center gap-3 group min-w-0 flex-1">
              <span className="text-4xl sm:text-5xl shrink-0" aria-hidden="true"><Flag country={data.champion} width={46} /></span>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5B842]">{V.championLabel}</div>
                <div className="font-heading text-xl sm:text-2xl lg:text-3xl font-black uppercase text-slate-50 group-hover:text-[#E5B842] break-words leading-tight" style={{ transition: "color 0.15s ease" }}>{data.champion.name}</div>
              </div>
            </Link>
            {V.shareId && <div className="relative ml-auto shrink-0"><ShareButton kind="champion" id={V.shareId} testid="share-champion-btn" /></div>}
          </div>
        )}

        {/* GROUP STAGE */}
        <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">Group Stage</h2>
        <div className="grid lg:grid-cols-2 gap-6 mb-4">
          {data.groups.map((g) => <GroupTable key={g.code} group={g} />)}
        </div>
        <div className="flex items-center gap-4 text-[11px] font-mono text-slate-500 mb-10">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> Q = qualifies for knockouts (top 2)</span>
          <span className="hidden sm:inline">TPF/TPA/TPD = Team Points For / Against / Difference · PTS = points</span>
        </div>

        {/* KNOCKOUT BRACKET (SF → Final) */}
        {data.bracket.filter((b) => b.round !== "DEMO").length > 0 && (
          <>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4"><Swords className="w-5 h-5 text-[#E5B842]" /> Knockout Stage</h2>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-3 mb-12" data-testid="championship-bracket">
              <div className="flex-1 lg:max-w-[420px]">
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-3">Semifinals</div>
                <div className="space-y-4">{sf.map((k) => <KoCard key={k.bracket_index} ko={k} />)}</div>
              </div>
              <div className="hidden lg:flex items-center text-slate-700"><ArrowRight className="w-6 h-6" /></div>
              <div className="flex lg:hidden justify-center text-slate-700"><ChevronDown className="w-6 h-6" /></div>
              <div className="flex-1 lg:max-w-[420px] lg:self-center">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#E5B842] mb-3">Final</div>
                {final ? <KoCard ko={final} big /> : <div className="rounded-xl border border-dashed border-[#2b2512] p-6 text-center text-slate-600 text-sm">Awaiting finalists</div>}
              </div>
            </div>
          </>
        )}

        {/* EXTRA DUELS — DEMONSTRATION */}
        {data.demo_knockout && (
          <div className="mb-12" data-testid="demo-knockout">
            <div className="rounded-xl bg-fuchsia-950/15 border border-fuchsia-500/30 px-4 py-2.5 mb-4 flex items-start gap-2">
              <Info className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-300 font-mono"><b className="text-fuchsia-300 uppercase tracking-wider">Demonstration.</b> A permanent, clearly-labeled staged knockout (level after 11 regulation duels) that shows how ArenaBook Extra Duels decide a tie. It does <b>not</b> affect the championship standings or results.</p>
            </div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4"><Zap className="w-5 h-5 text-fuchsia-400" /> Extra Duels — Demonstration</h2>
            <Card className="p-5">
              <div className="flex items-center justify-center gap-6 sm:gap-10 mb-5">
                <div className="text-center">
                  <div className="text-3xl" aria-hidden="true"><Flag country={data.demo_knockout.home} width={34} /></div>
                  <div className="font-heading font-bold text-slate-100">{data.demo_knockout.home?.sporty}</div>
                </div>
                <div className="text-center">
                  <div className="font-mono text-[10px] uppercase text-slate-500">After regulation</div>
                  <div className="font-heading text-xl font-black text-slate-400">
                    {data.demo_knockout.regulation && typeof data.demo_knockout.regulation === "object"
                      ? `${data.demo_knockout.regulation.home} – ${data.demo_knockout.regulation.away}`
                      : (data.demo_knockout.regulation || "Level")}
                  </div>
                  <div className="font-mono text-[10px] uppercase text-fuchsia-400 mt-2">After Extra Duels</div>
                  <div className="font-heading text-3xl font-black text-[#E5B842]" data-testid="demo-ko-final">{data.demo_knockout.final_home_points} – {data.demo_knockout.final_away_points}</div>
                  <div className="text-[9px] font-mono uppercase text-slate-600">Team Points</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl" aria-hidden="true"><Flag country={data.demo_knockout.away} width={34} /></div>
                  <div className="font-heading font-bold text-slate-100">{data.demo_knockout.away?.sporty}</div>
                </div>
              </div>
              {data.demo_knockout.duels.map((d) => (
                <div key={d.round_number} className="rounded-lg bg-[#070707] border border-fuchsia-500/15 p-3 mb-2" data-testid={`demo-duel-${d.round_number}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-fuchsia-400 inline-flex items-center gap-1"><Zap className="w-3 h-3" /> Extra Duel #{d.round_number}</span>
                    <span className="inline-flex items-center gap-1 font-heading font-bold text-emerald-300 text-xs uppercase">
                      <Crown className="w-3 h-3 text-[#E5B842]" /> {d.winner_side} +{d.team_points} TP
                      <span className="text-slate-500 font-mono normal-case ml-1">settled @ {d.winning_total_odds}</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {["home_coupon", "away_coupon"].map((side) => (
                      <div key={side} className={d.winner_side === (side === "home_coupon" ? "HOME" : "AWAY") ? "opacity-100" : "opacity-70"}>
                        <div className="font-mono text-[10px] uppercase text-slate-500 mb-0.5">
                          {side === "home_coupon" ? data.demo_knockout.home?.sporty : data.demo_knockout.away?.sporty} · {d[side].total_odds_str}× coupon · {d[side].potential_team_points} TP
                        </div>
                        {d[side].selections.map((s, i) => (
                          <div key={i} className="text-slate-300 truncate">{s.label} <span className="text-slate-500">@ {s.odds_str}</span></div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mt-1 text-[10px] font-mono text-slate-600 text-center">Odds are the ArenaBook coupon total — not a score. Winner is taken from settlement, never from the higher odds.</div>
            </Card>
          </div>
        )}

        {/* RESULTS & FIXTURES — stage-filtered IA */}
        <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">Results &amp; Fixtures</h2>
        <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512] mb-4" data-testid="championship-stage-tabs">
          {STAGE_TABS.map((s) => (
            <button key={s.key} data-testid={`stage-tab-${s.key}`} onClick={() => setStage(s.key)}
              className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-heading font-bold uppercase tracking-wide ${stage === s.key ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{s.label}</button>
          ))}
        </div>
        <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden" data-testid="championship-fixtures">
          {shown.length === 0 ? <div className="p-6 text-slate-500 text-sm">No fixtures in this stage.</div> :
            shown.map((m) => (
              <Link key={m.id} to={`/match/${m.id}`} data-testid={`champ-fixture-${m.public_match_id}`}
                className="flex items-center gap-3 px-4 sm:px-5 py-2.5 border-t border-[#2b2512]/60 first:border-t-0 hover:bg-[#161616]/60 text-sm"
                style={{ transition: "background-color 0.15s ease" }}>
                <span className="w-44 shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-500 hidden md:block truncate">{m.context_label}</span>
                <span className="text-lg" aria-hidden="true"><Flag country={m.home_club} width={22} /></span>
                <span className="font-semibold text-slate-100 w-12">{m.home_club?.short_name}</span>
                <span className="font-heading font-black tabular-nums text-[#E5B842] w-14 text-center">{m.status === "FINAL" ? `${m.home_team_points}–${m.away_team_points}` : "vs"}</span>
                <span className="font-semibold text-slate-100 w-12">{m.away_club?.short_name}</span>
                <span className="text-lg" aria-hidden="true"><Flag country={m.away_club} width={22} /></span>
                <span className="ml-auto font-mono text-[10px] uppercase text-slate-500">{m.status.replace(/_/g, " ")}</span>
              </Link>
            ))}
        </div>

        {/* NATIONS HISTORY — past-editions register (completed editions only) */}
        {V.showHistory && (
          <div className="mt-14" data-testid={`${variant.toLowerCase()}-history`}>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-5 h-5 text-[#E5B842]" />
              <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">{V.historyTitle}</h2>
            </div>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-5">{V.historySubtitle}</p>
            {!history ? (
              <div className="rounded-xl border border-[#2b2512] p-6 text-slate-600 text-sm">Loading history…</div>
            ) : (history.editions || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#2b2512] p-8 text-center" data-testid={`${variant.toLowerCase()}-history-empty`}>
                <Crown className="w-7 h-7 text-slate-700 mx-auto mb-3" />
                <div className="font-heading text-lg font-bold uppercase text-slate-300">First edition in progress</div>
                <div className="text-sm text-slate-500 mt-1">{V.historyEmptyText}</div>
              </div>
            ) : (
              <div className="space-y-3" data-testid={`${variant.toLowerCase()}-history-list`}>
                {history.editions.map((e) => (
                  <div key={e.competition_id} data-testid={`${variant.toLowerCase()}-history-${e.year || e.competition_id}`}
                    className="relative overflow-hidden rounded-xl foil-border bg-gradient-to-r from-[#1a1710] to-[#070707] p-4 sm:p-5">
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 150% at 95% 0%, rgba(212,175,55,0.12) 0%, transparent 60%)" }} />
                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-heading text-2xl sm:text-3xl font-black text-[#E5B842] tabular-nums">{e.year || "—"}</span>
                        <Trophy className="w-5 h-5 text-[#E5B842]/70" />
                      </div>
                      <Link to={e.champion ? `/nations/${e.champion.country_iso3}` : "#"}
                        data-testid={`${variant.toLowerCase()}-history-champion-${e.year || e.competition_id}`}
                        className="group flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-3xl sm:text-4xl shrink-0" aria-hidden="true"><Flag country={e.champion} width={36} /></span>
                        <div className="min-w-0">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E5B842]">Champion</div>
                          <div className="font-heading text-lg sm:text-xl font-black uppercase text-slate-50 group-hover:text-[#E5B842] break-words leading-tight" style={{ transition: "color 0.15s ease" }}>{e.champion?.name}</div>
                        </div>
                      </Link>
                      {e.finalist && e.final_result && (
                        <div className="shrink-0 sm:text-right sm:border-l sm:border-[#2b2512] sm:pl-5">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Final</div>
                          <div className="flex items-center gap-2 sm:justify-end">
                            <span className="font-heading text-base font-black tabular-nums text-slate-200">{e.final_result.winner_points}–{e.final_result.loser_points}</span>
                            <span className="text-[9px] font-mono uppercase text-slate-600">TP</span>
                          </div>
                          <div className="flex items-center gap-1.5 sm:justify-end mt-0.5 text-slate-400">
                            <span className="text-base" aria-hidden="true"><Flag country={e.finalist} width={20} /></span>
                            <span className="text-sm font-semibold break-words">{e.finalist.name}</span>
                          </div>
                          {e.final_result.extra_rounds > 0 && (
                            <div className="text-[9px] font-mono uppercase text-fuchsia-400 mt-0.5">decided by extra duels</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
